/* ─────────────────────────────────────────────────────────────────────────
   Team roster + Leadership PDI computation.
   Shared between the Team Management view and the COP Team Pulse card so
   the operating picture is single-sourced.
   ─────────────────────────────────────────────────────────────────────── */

import { ROLE_BY_ID } from "./rbac";

export const REQUIRES_CATEGORY_FOCUS = new Set(["CATEGORY_LEADER", "BUYER"]);

export const SEED_TEAM = [
  { id: "u-001", name: "Eleanor Whitlock",  email: "eleanor.whitlock@procureo.live",  role: "DIRECTOR",        segments: [], hired: "2019-04-11" },
  { id: "u-002", name: "James Okafor",      email: "james.okafor@procureo.live",      role: "MANAGER",         segments: [], hired: "2020-09-02" },
  { id: "u-003", name: "Priya Raman",       email: "priya.raman@procureo.live",       role: "MANAGER",         segments: [], hired: "2021-01-18" },
  { id: "u-004", name: "Daniel Reyes",      email: "daniel.reyes@procureo.live",      role: "CATEGORY_LEADER", segments: ["43000000", "81000000"], hired: "2021-07-06" },
  { id: "u-005", name: "Mei-Lin Tanaka",    email: "mei.tanaka@procureo.live",        role: "CATEGORY_LEADER", segments: ["24000000"], hired: "2022-03-14" },
  { id: "u-006", name: "Arjun Sharma",      email: "arjun.sharma@procureo.live",      role: "CATEGORY_LEADER", segments: ["80000000"], hired: "2022-05-30" },
  { id: "u-007", name: "Sofia Larsson",     email: "sofia.larsson@procureo.live",     role: "BUYER",           segments: ["43000000"], hired: "2023-02-20" },
  { id: "u-008", name: "Marcus Cole",       email: "marcus.cole@procureo.live",       role: "BUYER",           segments: ["80000000"], hired: "2023-06-12" },
  { id: "u-009", name: "Yuki Hoshino",      email: "yuki.hoshino@procureo.live",      role: "BUYER",           segments: ["77000000"], hired: "2024-01-08" },
  { id: "u-010", name: "Theodora Pappas",   email: "theo.pappas@procureo.live",       role: "BUYER",           segments: [], hired: "2024-09-04" },
];

// Composite Leadership PDI:
//   per operative = role-base PDI + discipline adjust (+6 if needs focus and has it; −10 if missing).
export function computeLeadershipPdi(team) {
  if (!team || team.length === 0) {
    return { score: 0, tier: "GREEN", coverage: 0, focusMissing: 0, focusEligible: 0 };
  }
  let total = 0;
  let missing = 0;
  team.forEach((u) => {
    const base = ROLE_BY_ID[u.role]?.pdiContribution ?? 70;
    const needsFocus = REQUIRES_CATEGORY_FOCUS.has(u.role);
    const hasFocus = (u.segments || []).length > 0;
    const adj = needsFocus ? (hasFocus ? 6 : -10) : 0;
    total += Math.max(40, Math.min(100, base + adj));
    if (needsFocus && !hasFocus) missing += 1;
  });
  const score = Math.round(total / team.length);
  const tier  = score >= 85 ? "GREEN" : score >= 75 ? "YELLOW" : "AMBER";
  const focusEligible = team.filter((u) => REQUIRES_CATEGORY_FOCUS.has(u.role)).length;
  const focusAssigned = focusEligible - missing;
  const coverage = focusEligible ? Math.round((focusAssigned / focusEligible) * 100) : 100;
  return { score, tier, coverage, focusMissing: missing, focusEligible };
}

// Per-operative Pulse Score — drives the personnel drill-down drawer.
// Buyer & Category-Leader scores carry +/− deltas tied to category focus presence.
export function operativePulse(user) {
  const base = ROLE_BY_ID[user.role]?.pdiContribution ?? 70;
  const needsFocus = REQUIRES_CATEGORY_FOCUS.has(user.role);
  const hasFocus = (user.segments || []).length > 0;
  const adj = needsFocus ? (hasFocus ? 6 : -10) : 0;
  return Math.max(40, Math.min(100, base + adj));
}
